import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Tab,
  Tabs,
  IconButton,
  Typography,
  Divider,
  useTheme,
} from '@mui/material';
import {
  Close as CloseIcon,
  Person as PersonIcon,
  Store as StoreIcon,
} from '@mui/icons-material';
import LoginForm from './LoginForm';
import RegisterForm from './RegisterForm';
import { useTranslation } from '../../contexts/TranslationContext';

interface AuthModalProps {
  open: boolean;
  onClose: () => void;
  initialTab?: 'login' | 'register';
  disableRedirect?: boolean;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`auth-tabpanel-${index}`}
      aria-labelledby={`auth-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
  );
}

const AuthModal: React.FC<AuthModalProps> = ({ open, onClose, initialTab = 'login', disableRedirect = false }) => {
  const [tabValue, setTabValue] = useState(initialTab === 'login' ? 0 : 1);
  const { t } = useTranslation();
  const theme = useTheme();

  // Update tab when initialTab prop changes
  useEffect(() => {
    setTabValue(initialTab === 'login' ? 0 : 1);
  }, [initialTab]);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const handleClose = () => {
    setTabValue(0); // Reset to login tab
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          minHeight: '500px',
        },
      }}
    >
      <DialogTitle sx={{ pb: 0 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography
            variant="h5"
            fontWeight="bold"
            sx={{
              color: theme.palette.primary.main,
            }}
          >
            {tabValue === 0 ? t('auth.tabs.signIn') : t('auth.tabs.signUp')}
          </Typography>
          <IconButton
            onClick={handleClose}
            sx={{
              color: 'text.secondary',
              '&:hover': {
                bgcolor: 'grey.100',
              },
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ px: 0 }}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs
            value={tabValue}
            onChange={handleTabChange}
            centered
            sx={{
              '& .MuiTabs-indicator': {
                backgroundColor: theme.palette.primary.main,
                height: 3,
                borderRadius: 3,
              },
              '& .MuiTab-root': {
                textTransform: 'none',
                fontWeight: 600,
                fontSize: '1rem',
                minHeight: 48,
                borderRadius: 999,
                mx: 1,
                '&.Mui-selected': {
                  color: theme.palette.primary.main,
                },
              },
            }}
          >
            <Tab
              label={t('auth.tabs.signIn')}
              icon={<PersonIcon />}
              iconPosition="start"
              sx={{ flex: 1 }}
            />
            <Tab
              label={t('auth.tabs.signUp')}
              icon={<StoreIcon />}
              iconPosition="start"
              sx={{ flex: 1 }}
            />
          </Tabs>
        </Box>

        <TabPanel value={tabValue} index={0}>
          <LoginForm onSuccess={handleClose} disableRedirect={disableRedirect} />
        </TabPanel>

        <TabPanel value={tabValue} index={1}>
          <RegisterForm onSuccess={handleClose} />
        </TabPanel>
      </DialogContent>

      <Divider />

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Typography variant="body2" color="text.secondary" textAlign="center" sx={{ width: '100%' }}>
          {t('auth.agree')}
        </Typography>
      </DialogActions>
    </Dialog>
  );
};

export default AuthModal;
